import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  },
  withCredentials: true,
  timeout: 10000
});

// Add cache-busting timestamp to every GET request to prevent Cloudflare mobile/desktop caching
apiClient.interceptors.request.use(
  (config) => {
    // Cookies are sent automatically with credentials: 'include'
    if (config.method === 'get') {
      config.params = { ...config.params, _t: Date.now() };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Public API
export const submitExhibitorEnquiry = async (data) => {
  const response = await apiClient.post('/exhibitor-enquiry', data);
  return response.data;
};

export const submitContactMessage = async (data) => {
  const response = await apiClient.post('/contact', data);
  return response.data;
};

export const getSiteSettings = async () => {
  const response = await apiClient.get('/settings');
  return response.data;
};

// Admin API - Google Auth handles login via AdminAuthContext

export const getAdminStats = async () => {
  const response = await apiClient.get('/admin/stats');
  return response.data;
};

export const getExhibitorEnquiries = async (params = {}) => {
  const response = await apiClient.get('/admin/exhibitor-enquiries', { params });
  return response.data;
};

export const updateEnquiryStatus = async (id, status) => {
  const response = await apiClient.patch(`/admin/exhibitor-enquiries/${id}`, { status });
  return response.data;
};

export const deleteEnquiry = async (id) => {
  const response = await apiClient.delete(`/admin/exhibitor-enquiries/${id}`);
  return response.data;
};

export const getContactMessages = async (params = {}) => {
  const response = await apiClient.get('/admin/contact-messages', { params });
  return response.data;
};

export const updateMessageStatus = async (id, status) => {
  const response = await apiClient.patch(`/admin/contact-messages/${id}`, { status });
  return response.data;
};

export const deleteMessage = async (id) => {
  const response = await apiClient.delete(`/admin/contact-messages/${id}`);
  return response.data;
};

export const updateSiteSettings = async (settings) => {
  const response = await apiClient.put('/admin/settings', settings);
  return response.data;
};

// Partners API
export const getPartners = async () => {
  const response = await apiClient.get('/partners');
  return response.data;
};

export const getAdminPartners = async () => {
  const response = await apiClient.get('/admin/partners');
  return response.data;
};

export const createPartner = async (partner) => {
  const response = await apiClient.post('/admin/partners', partner);
  return response.data;
};

export const updatePartner = async (id, partner) => {
  const response = await apiClient.put(`/admin/partners/${id}`, partner);
  return response.data;
};

export const deletePartner = async (id) => {
  const response = await apiClient.delete(`/admin/partners/${id}`);
  return response.data;
};

// Success Stories API
export const getSuccessStories = async () => {
  const response = await apiClient.get('/success-stories');
  return response.data;
};

export const getAdminSuccessStories = async () => {
  const response = await apiClient.get('/admin/success-stories');
  return response.data;
};

export const createSuccessStory = async (story) => {
  const response = await apiClient.post('/admin/success-stories', story);
  return response.data;
};

export const updateSuccessStory = async (id, story) => {
  const response = await apiClient.put(`/admin/success-stories/${id}`, story);
  return response.data;
};

export const deleteSuccessStory = async (id) => {
  const response = await apiClient.delete(`/admin/success-stories/${id}`);
  return response.data;
};

// Previous Events API
export const getPreviousEvents = async () => {
  const response = await apiClient.get('/previous-events');
  return response.data;
};

export const getAdminPreviousEvents = async () => {
  const response = await apiClient.get('/admin/previous-events');
  return response.data;
};

export const createPreviousEvent = async (event) => {
  const response = await apiClient.post('/admin/previous-events', event);
  return response.data;
};

export const updatePreviousEvent = async (id, event) => {
  const response = await apiClient.put(`/admin/previous-events/${id}`, event);
  return response.data;
};

export const deletePreviousEvent = async (id) => {
  const response = await apiClient.delete(`/admin/previous-events/${id}`);
  return response.data;
};

// Gallery Images API
export const getGalleryImages = async () => {
  const response = await apiClient.get('/gallery-images');
  return response.data;
};

export const getAdminGalleryImages = async () => {
  const response = await apiClient.get('/admin/gallery-images');
  return response.data;
};

export const createGalleryImage = async (image) => {
  const response = await apiClient.post('/admin/gallery-images', image);
  return response.data;
};

export const updateGalleryImage = async (id, image) => {
  const response = await apiClient.put(`/admin/gallery-images/${id}`, image);
  return response.data;
};

export const deleteGalleryImage = async (id) => {
  const response = await apiClient.delete(`/admin/gallery-images/${id}`);
  return response.data;
};

// Gallery Videos API
export const getGalleryVideos = async () => {
  const response = await apiClient.get('/gallery-videos');
  return response.data;
};

export const getAdminGalleryVideos = async () => {
  const response = await apiClient.get('/admin/gallery-videos');
  return response.data;
};

export const createGalleryVideo = async (video) => {
  const response = await apiClient.post('/admin/gallery-videos', video);
  return response.data;
};

export const updateGalleryVideo = async (id, video) => {
  const response = await apiClient.put(`/admin/gallery-videos/${id}`, video);
  return response.data;
};

export const deleteGalleryVideo = async (id) => {
  const response = await apiClient.delete(`/admin/gallery-videos/${id}`);
  return response.data;
};

// Packages API
export const getPackages = async () => {
  const response = await apiClient.get('/packages');
  return response.data;
};

export const getAdminPackages = async () => {
  const response = await apiClient.get('/admin/packages');
  return response.data;
};

export const createPackage = async (pkg) => {
  const response = await apiClient.post('/admin/packages', pkg);
  return response.data;
};

export const updatePackage = async (id, pkg) => {
  const response = await apiClient.put(`/admin/packages/${id}`, pkg);
  return response.data;
};

export const deletePackage = async (id) => {
  const response = await apiClient.delete(`/admin/packages/${id}`);
  return response.data;
};

// Notification Settings API
export const getNotificationSettings = async () => {
  const response = await apiClient.get('/admin/notification-settings');
  return response.data;
};

export const updateNotificationSettings = async (settings) => {
  const response = await apiClient.put('/admin/notification-settings', settings);
  return response.data;
};

// Speakers API
export const getSpeakers = async () => {
  const response = await apiClient.get('/speakers');
  return response.data;
};

export const getAdminSpeakers = async () => {
  const response = await apiClient.get('/admin/speakers');
  return response.data;
};

export const createSpeaker = async (speaker) => {
  const response = await apiClient.post('/admin/speakers', speaker);
  return response.data;
};

export const updateSpeaker = async (id, speaker) => {
  const response = await apiClient.put(`/admin/speakers/${id}`, speaker);
  return response.data;
};

export const deleteSpeaker = async (id) => {
  const response = await apiClient.delete(`/admin/speakers/${id}`);
  return response.data;
};

export default apiClient;
